// Team lead guardrail (DRAFT, OFF by default). Nothing in Resi enforces this yet.
// Rule: an agent must be active in Resi every weekday (Mon-Fri). Activity = a Resi login OR working a lead that day
// (configurable). If any weekday of a week is missed, team lead eligibility pauses for the whole next week.
// Eligibility comes back only after a full compliant week.
// Open decisions for Kelly (see report): login vs lead worked, time zone, holidays, PTO, reassigning existing leads.

export const TEAM_LEAD_GUARDRAIL_ENABLED = false;

export const GUARDRAIL_CONFIG = {
  timeZone: 'America/New_York',
  countLoginAsActivity: true, // false = a lead must be worked
  countLeadWorkAsActivity: true,
  holidays: [], // 'YYYY-MM-DD' weekdays that don't count (none decided yet)
};

const pad = (n) => String(n).padStart(2, '0');
export const ymd = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const parse = (key) => new Date(`${key}T12:00:00Z`);
const addDays = (key, n) => { const d = parse(key); d.setUTCDate(d.getUTCDate() + n); return ymd(d); };

// Today's date key in the guardrail time zone.
export const todayKey = (now = new Date(), timeZone = GUARDRAIL_CONFIG.timeZone) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);

// Monday (YYYY-MM-DD) of the week containing dateKey.
export const weekStart = (dateKey) => { const dow = parse(dateKey).getUTCDay(); return addDays(dateKey, dow === 0 ? -6 : 1 - dow); };

export const weekdaysOf = (mondayKey, holidays = []) =>
  [0, 1, 2, 3, 4].map((i) => addDays(mondayKey, i)).filter((k) => !holidays.includes(k));

export const isActiveDay = (key, { loginDays = {}, leadWorkDays = {} } = {}, cfg = GUARDRAIL_CONFIG) =>
  (cfg.countLoginAsActivity && !!loginDays[key]) || (cfg.countLeadWorkAsActivity && !!leadWorkDays[key]);

// Status of one week. Days after `today` are "upcoming", not missed.
export function weekStatus(mondayKey, activity, today, cfg = GUARDRAIL_CONFIG) {
  const days = weekdaysOf(mondayKey, cfg.holidays).map((key) => ({
    key, state: key > today ? 'upcoming' : isActiveDay(key, activity, cfg) ? 'active' : (key === today ? 'today' : 'missed'),
  }));
  const missed = days.filter((d) => d.state === 'missed').length;
  const complete = days.every((d) => d.state === 'active');
  return { weekOf: mondayKey, days, missed, compliant: missed === 0, complete };
}

// Eligibility for the current week, decided by last week: any missed weekday last week = paused this week.
// A paused week that is itself fully compliant restores eligibility the week after.
export function eligibility(activity, now = new Date(), cfg = GUARDRAIL_CONFIG) {
  const today = todayKey(now, cfg.timeZone);
  const thisMon = weekStart(today);
  const lastWeek = weekStatus(addDays(thisMon, -7), activity, today, cfg);
  const thisWeek = weekStatus(thisMon, activity, today, cfg);
  if (!TEAM_LEAD_GUARDRAIL_ENABLED && !cfg.forceEnabled) return { enabled: false, eligible: true, lastWeek, thisWeek };
  const eligible = lastWeek.compliant;
  return { enabled: true, eligible, pausedWeekOf: eligible ? null : thisMon, resumesWeekOf: eligible ? null : addDays(thisMon, 7), lastWeek, thisWeek };
}

// Future lead routing should call this. Not wired into routing yet.
export function canReceiveTeamLeads(user = {}, now = new Date(), cfg = GUARDRAIL_CONFIG) {
  if (!user.teamLeadCertified) return false;
  const a = user.activity || {};
  return eligibility({ loginDays: a.loginDays, leadWorkDays: a.leadWorkDays }, now, cfg).eligible;
}
